import React, { useEffect, useRef, useState } from "react"
import { AppFunctionComponent } from "../../../../../types"
import { TestimonialsConfig } from "./testimonials.interface"
import {
  BackgroundWrapper,
  SectionArticle,
  SectionHeading,
  SectionLayout,
} from "../../section.component"
import SwiperCore from "swiper"
import { SectionContainer } from "../../../../../resolvers/text/text.resolver"
import SingleTestimonial from "./single-testimonial.component"
import styled from "styled-components"
import { PrimaryHeading } from "../../../heading.component"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  ControlsNextIcon,
  ControlsPrevIcon,
} from "../../../slider/slider-arrows-text.component"
import { SliderButtonStyle } from "../quick-start/quick-start.styled"
import useTranslationHook from "../../../../../hooks/use-translation.hook"
import { BasicButtonLink } from "../../../../../ui"

type TestimonialsProps = {
  config: TestimonialsConfig
}

const TestimonialListContainer = styled(Swiper)`
  display: flex;
  width: 100%;
  /* margin-top: 32px; */

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    margin-top: 80px;
    margin-bottom: 0;
  }
`

const TestimonialSectionContainer = styled(SectionContainer)`
  width: 100%;

  padding: 0;
  padding-bottom: 39px;

  ${SectionHeading} {
    width: 100%;
    max-width: 100%;
    ${PrimaryHeading} {
      width: 100%;
      text-align: center !important;
    }
  }

  ${SectionArticle} {
    width: 100%;
    max-width: 100%;
    p {
      text-align: center;
      font-size: 18px;
      line-height: 24px;
      margin-top: 30px !important;
    }
  }
`

const Testimonials: AppFunctionComponent<TestimonialsProps> = ({ config }) => {
  const [swiper, setSwiper] = useState<SwiperCore>()
  const [activeIndex, setActiveIndex] = useState(0)
  const ref = useRef<HTMLDivElement | null>(null)
  const { translate } = useTranslationHook()

  useEffect(() => {
    if (swiper) {
      setActiveIndex(swiper.activeIndex)
    }
  }, [swiper?.activeIndex])

  return (
    <BackgroundWrapper id="reviews" background={config.testimonials.background}>
      <TestimonialSectionContainer layout={SectionLayout.TextLeftAligned}>
        <SectionHeading>
          <PrimaryHeading>{config.testimonials.heading}</PrimaryHeading>
        </SectionHeading>
        <SectionArticle>
          <p>{config.testimonials.text}</p>
        </SectionArticle>
      </TestimonialSectionContainer>
      <TestimonialListContainer
        slidesPerView={1}
        spaceBetween={32}
        ref={ref}
        onSwiper={(swiper) => setSwiper(swiper)}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.activeIndex)
        }}
        navigation={{
          nextEl: ".swiper-arrow-right-embed",
          prevEl: ".swiper-arrow-left-embed",
        }}
        breakpoints={{
          768: {
            slidesPerView: config.testimonials.list.length,
            spaceBetween: 32,
          },
        }}
      >
        {config.testimonials.list.map((element) => (
          <SwiperSlide key={element.name}>
            <SingleTestimonial
              key={element.name}
              name={element.name}
              description={element.description}
              product={element.product}
              numberOfStars={element.numberOfStars}
            />
          </SwiperSlide>
        ))}
      </TestimonialListContainer>
      <StyledButtonsWrapper
        id="sneak-peaks"
        style={{ marginLeft: "0px", marginRight: "0px", width: "100%" }}
      >
        {/* <StyledExtraLink to={"#newsletter"} aria-label={"join"}>
            {translate("Join the Waitlist")}
          </StyledExtraLink> */}
        <StyledControlsButtonWrapper>
          <SliderButtonStyle
            className="swiper-arrow-left-embed"
            disabledPrev={activeIndex === 0}
            onClick={() => {
              const objectPosition = ref.current?.getBoundingClientRect()
              window.scrollTo(
                objectPosition!.left,
                objectPosition!.top + window.scrollY - 60
              )
            }}
          >
            <ControlsPrevIcon />
            {translate("PREV")}
          </SliderButtonStyle>
          <SliderButtonStyle
            className="swiper-arrow-right-embed"
            disabledNext={activeIndex === config.testimonials.list.length - 1}
            onClick={() => {
              const objectPosition = ref.current?.getBoundingClientRect()
              window.scrollTo(
                objectPosition!.left,
                objectPosition!.top + window.scrollY - 60
              )
            }}
          >
            {translate("NEXT")}
            <ControlsNextIcon />
          </SliderButtonStyle>
        </StyledControlsButtonWrapper>
      </StyledButtonsWrapper>
      {config.testimonials.button && (
        <StyledLinkWrapper>
          <BasicButtonLink
            to={config.testimonials.button.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {config.testimonials.button.text}
          </BasicButtonLink>
        </StyledLinkWrapper>
      )}
    </BackgroundWrapper>
  )
}

export default Testimonials

const StyledButtonsWrapper = styled(SectionContainer)`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(4, 240px);
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  margin-bottom: 2rem;
  max-width: unset;
  padding: 0;
  margin-top: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    left: 0;
    gap: 5rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    display: none !important;
  }
`

const StyledControlsButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  height: 100%;
  grid-column: 3/5;
  width: 100%;

  button {
    cursor: pointer;
    min-width: 240px;

    @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
      min-width: unset;
    }
  }
`

const StyledLinkWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 80px;

  @media (max-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    margin-top: 48px;
  }

  a {
    text-decoration: none;
  }
`
